import { LitElement, html } from 'lit';

import { navigate } from '../../lib/router/index.js';
import { api } from '../api/index.js';
import { confirmAction } from '../components/plane-confirm.js';
import { loadState } from '../components/load-state.js';
import { OPERATOR_RULES, PERSONA } from '../i18n/labels.js';
import { assistantSchema, operatorParams, validate } from '../params-form.js';
import { newestVersion } from '../format.js';
import { formStyles } from '../../styles/shared/forms.js';
import { markdownStyles } from '../../styles/shared/markdown.js';
import { pageStyles } from '../../styles/shared/page.js';
import { TableLabels } from '../table-labels.js';
import { tableStyles } from '../../styles/shared/tables.js';
import { templateEditorPageStyles } from '../../styles/pages/template-editor-page.js';

/**
 * The editor of an agent template, one assistant: its name, the id it proposes for the assistant
 * it creates, its image version, the parameters of one assistant of that version (generated from
 * its params schema, model included) and the three persona files.
 *
 * `templateId` null is a new template: empty, or (`?fromInstance=<ref>`) with the operator's part
 * of the desired revision of an instance (image version, model, thinking, operator rules), which
 * the backend takes from the instance itself. What its customer wrote is never copied. With
 * `?duplicate=<id>` it starts as a copy of another template: the way to change one that comes with
 * the plane (`builtIn`), which is read-only here. Saving a template changes none of the instances made from it: "Update
 * instances" brings those made from an older version to the current one, the operator's choices
 * only (model, thinking, operator rules, image version), never what their customer wrote.
 */
class TemplateEditorPage extends LitElement {
  static properties = {
    templateId: { type: String },
    loaded: { state: true },
    content: { state: true },
    schema: { state: true },
    errors: { state: true },
    saving: { state: true },
    saveError: { state: true },
    updateResult: { state: true },
  };

  static styles = [
    pageStyles,
    formStyles,
    tableStyles,
    markdownStyles,
    templateEditorPageStyles,
  ];

  constructor() {
    super();
    new TableLabels(this);
    this.templateId = null;
    this.errors = {};
    this.saving = false;
    this.saveError = null;
    // loads once: an editor must not change under the user's hands
    this.loader = { value: undefined, error: null, refresh: () => this.load() };
  }

  connectedCallback() {
    super.connectedCallback();
    this.load();
  }

  updated(changed) {
    if (changed.has('templateId') && changed.get('templateId') !== undefined) {
      this.load();
    }
  }

  /** The versions to propose: those the clawlets declared, the ones an instance can run on. */
  async knownVersions() {
    const servers = await api.servers();
    return [...new Set(servers.flatMap((server) => server.imageVersions ?? []))];
  }

  async load() {
    this.loader = { ...this.loader, value: undefined, error: null };
    this.requestUpdate();
    try {
      const versions = await this.knownVersions();
      let template = null;
      let content;
      let fromInstance = null;
      if (this.templateId) {
        template = await api.template(this.templateId);
        content = {
          name: template.name,
          description: template.description,
          imageVersion: template.imageVersion,
          assistantId: template.assistantId,
          params: template.params,
          persona: template.persona,
          operatorRules: template.operatorRules,
        };
      } else {
        const query = new URLSearchParams(window.location.search);
        const from = query.get('fromInstance');
        const duplicate = query.get('duplicate');
        content = { name: '', description: '', imageVersion: newestVersion(versions) ?? '', assistantId: '', params: {}, persona: { soul: '', agents: '', user: '' }, operatorRules: '' };
        if (duplicate) {
          const original = await api.template(duplicate);
          content = {
            name: `${original.name} (copy)`,
            description: original.description,
            imageVersion: original.imageVersion,
            assistantId: original.assistantId,
            params: original.params,
            persona: original.persona,
            operatorRules: original.operatorRules,
          };
        } else if (from) {
          const instance = await api.instance(from);
          if (instance.desiredRevision) {
            const revision = await api.revision(from, instance.desiredRevision.id);
            const schema = assistantSchema((await api.imageVersion(instance.imageVersion)).paramsSchema);
            // only the operator's part of its assistant, as the backend takes it: the rest is the customer's
            const ids = Object.keys(revision.params.assistants ?? {});
            const assistant = ids.length === 1 ? revision.params.assistants[ids[0]] : {};
            const params = Object.fromEntries(operatorParams(schema).filter((name) => name in assistant).map((name) => [name, assistant[name]]));
            content = { ...content, imageVersion: instance.imageVersion, assistantId: ids.length === 1 ? ids[0] : '', params, operatorRules: revision.operatorRules };
            fromInstance = instance.ref ?? from;
          }
        }
      }
      this.content = structuredClone(content);
      this.schema = content.imageVersion ? assistantSchema((await api.imageVersion(content.imageVersion)).paramsSchema) : null;
      this.loaded = { template, versions: [...new Set([...versions, content.imageVersion].filter(Boolean))], fromInstance };
      this.errors = {};
      this.loader = { ...this.loader, value: this.loaded };
    } catch (error) {
      this.loader = { ...this.loader, error };
    }
    this.requestUpdate();
  }

  async changeVersion(version) {
    this.content = { ...this.content, imageVersion: version };
    this.saveError = null;
    this.schema = null;
    try {
      const schema = assistantSchema((await api.imageVersion(version)).paramsSchema);
      // a later choice wins over an answer that arrives late
      if (this.content.imageVersion === version) {
        this.schema = schema;
      }
    } catch (error) {
      if (this.content.imageVersion === version) {
        this.saveError = error.status === 404 ? `Image version ${version} has no params schema.` : error.message;
      }
    }
  }

  async save() {
    if (!this.schema) {
      this.saveError = 'Choose an image version that has a params schema.';
      return;
    }
    this.errors = validate(this.schema, this.content.params);
    if (Object.keys(this.errors).length > 0) {
      this.saveError = 'Some parameters are not valid: see the fields marked in red.';
      return;
    }
    const pattern = this.idPattern();
    if (this.content.assistantId && pattern && !pattern.test(this.content.assistantId)) {
      this.saveError = 'An assistant id is lowercase letters, digits and -, neither first nor last.';
      return;
    }
    this.saving = true;
    this.saveError = null;
    try {
      // an id left empty: the backend makes one of the name
      const content = { ...this.content, assistantId: this.content.assistantId || null };
      if (this.templateId) {
        // the version it was opened at: a change saved by somebody else since is refused, not undone
        await api.updateTemplate(this.templateId, { ...content, version: this.loaded.template.version });
        navigate('/templates');
      } else {
        // the new template's own page: its version, its instances, and what to do next
        const fromInstance = this.loaded.fromInstance;
        const created = await api.createTemplate(fromInstance ? { ...content, fromInstance } : content);
        navigate(`/templates/${encodeURIComponent(created.id)}`);
      }
    } catch (error) {
      this.saveError = error.message;
    } finally {
      this.saving = false;
    }
  }

  /** The pattern of the assistant ids (protocol/schema/common.json, the same in every image version). */
  idPattern() {
    return /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/u;
  }

  async updateInstances(template) {
    if (
      !(await confirmAction({
        heading: `Bring ${template.behind} assistant${template.behind === 1 ? '' : 's'} to version ${template.version}?`,
        message: 'Each gets a new revision with your model, thinking and operator rules for the assistant made from the template, applied at once; what its customer wrote stays, and so do its other assistants.',
        confirmLabel: 'Update workspaces',
      }))
    ) {
      return;
    }
    this.saving = true;
    this.saveError = null;
    try {
      this.updateResult = await api.updateTemplateInstances(this.templateId);
      const template = await api.template(this.templateId);
      this.loaded = { ...this.loaded, template };
    } catch (error) {
      this.saveError = error.message;
    } finally {
      this.saving = false;
    }
  }

  renderUpdateResult() {
    const result = this.updateResult;
    if (!result) {
      return null;
    }
    const names = (list) => list.map((name, index) => html`${index > 0 ? ', ' : ''}<a href="/workspaces/${encodeURIComponent(name)}"><code>${name}</code></a>`);
    return html`
      <div class="notice" data-tone=${result.skipped.length > 0 ? 'warning' : 'ok'} role="status">
        <strong>Workspaces brought to version ${result.version}</strong>
        ${result.updated.length > 0 ? html`<div>New revision, applied: ${names(result.updated)}</div>` : null}
        ${result.unchanged.length > 0 ? html`<div>Nothing to change: ${names(result.unchanged)}</div>` : null}
        ${result.skipped.map((skipped) => html`<div>Left behind: <a href="/workspaces/${encodeURIComponent(skipped.instance)}"><code>${skipped.instance}</code></a>, ${skipped.reason}</div>`)}
        ${result.updated.length + result.unchanged.length + result.skipped.length === 0 ? html`<div>Every workspace was up to date.</div>` : null}
      </div>
    `;
  }

  renderCopies(template) {
    if (template.copies.length === 0) {
      return null;
    }
    return html`
      <section class="copies">
        <h2>
          Assistants made from it
          ${template.behind > 0
            ? html`<button class="primary small" ?disabled=${this.saving} @click=${() => this.updateInstances(template)}>
                Update ${template.behind} assistant${template.behind === 1 ? '' : 's'}
              </button>`
            : null}
        </h2>
        ${this.renderUpdateResult()}
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Assistant</th>
                <th>Customer</th>
                <th>Has the changes of</th>
              </tr>
            </thead>
            <tbody>
              ${template.copies.map(
                (copy) => html`
                  <tr>
                    <td><a href="/workspaces/${encodeURIComponent(copy.instance)}"><code>${copy.instance}</code></a></td>
                    <td><code>${copy.assistant}</code></td>
                    <td>${copy.customer ?? html`<span class="muted">none</span>`}</td>
                    <td>${copy.templateVersion === template.version ? 'the current version' : copy.templateVersion === 0 ? html`changed by hand <span class="muted">(behind)</span>` : html`version ${copy.templateVersion} <span class="muted">(behind)</span>`}</td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        </div>
      </section>
    `;
  }

  async archive(archived) {
    this.saving = true;
    this.saveError = null;
    try {
      const template = await api.archiveTemplate(this.templateId, archived);
      this.loaded = { ...this.loaded, template };
    } catch (error) {
      this.saveError = error.message;
    } finally {
      this.saving = false;
    }
  }

  render() {
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/templates">Templates</a> / ${this.templateId ? 'edit' : 'new'}</nav>`;
    const pending = loadState(this.loader, 'the template');
    if (pending) {
      return html`${crumbs}${pending}`;
    }
    const { template, versions, fromInstance } = this.loaded;
    // one that comes with the plane changes with the plane: an operator duplicates it
    const locked = this.saving || Boolean(template?.builtIn);
    const set = (field, value) => {
      this.content = { ...this.content, [field]: value };
    };
    return html`
      ${crumbs}
      <header class="page-head">
        <div>
          <h1>${template ? template.name : 'New template'}</h1>
          <p class="lead">
            ${template
              ? html`Version ${template.version}${template.archived ? ', archived' : ''}. ${template.assistants} assistant${template.assistants === 1 ? '' : 's'} of the workspaces came from it: saving changes none of them until you update them.`
              : 'A new workspace made from it gets it as revision 1. Its customer then changes the name, the picture, the persona and who can write to it; the model stays yours.'}
          </p>
        </div>
        ${template
          ? html`<div class="actions">
              <a class="button" href="/templates/new?duplicate=${encodeURIComponent(template.id)}">Duplicate</a>
              <button ?disabled=${this.saving} @click=${() => this.archive(!template.archived)}>${template.archived ? 'Put back in use' : 'Archive'}</button>
            </div>`
          : null}
      </header>
      ${template?.builtIn
        ? html`<div class="notice" data-tone="muted" role="status">
            <strong>Comes with plane</strong>Each update of plane may bring a new version of it: its workspaces are then behind, and you update them when you choose. To change it, <strong>Duplicate</strong> it and make the copy your own.
          </div>`
        : null}
      ${template?.archived ? html`<div class="notice" data-tone="muted" role="status"><strong>Archived</strong>No new workspace starts from it until it is put back in use.</div>` : null}
      ${template ? this.renderCopies(template) : null}
      ${fromInstance
        ? html`<div class="notice" data-tone="muted" role="status">
            <strong>From <code>${fromInstance}</code></strong>Its image version, model, thinking and operator rules come from the workspace as it runs now. The name, the persona and everything else its customer wrote stay theirs: write the template's own.
          </div>`
        : null}
      <form
        @submit=${(event) => {
          event.preventDefault();
          this.save();
        }}
      >
        <section>
          <h2>Template</h2>
          <div class="field">
            <label for="name">Name</label>
            <input id="name" type="text" required maxlength="100" ?disabled=${locked} .value=${this.content.name} @input=${(event) => set('name', event.target.value)} />
          </div>
          <div class="field">
            <label for="description">Description</label>
            <span class="help">What it is for, for whoever picks it for a new workspace.</span>
            <input id="description" type="text" maxlength="1000" ?disabled=${locked} .value=${this.content.description} @input=${(event) => set('description', event.target.value)} />
          </div>
          <div class="field">
            <label for="image-version">Image version</label>
            <input
              id="image-version"
              type="text"
              required
              maxlength="64"
              list="template-image-versions"
              ?disabled=${locked || fromInstance}
              .value=${this.content.imageVersion}
              @change=${(event) => this.changeVersion(event.target.value.trim())}
            />
            <datalist id="template-image-versions">${versions.map((version) => html`<option value=${version}></option>`)}</datalist>
          </div>
          <div class="field">
            <label for="assistant-id">Assistant id</label>
            <span class="help">
              The id a new workspace gives the assistant it makes from this template, for example <code>reception</code>: the operator may choose another when creating
              the workspace, and it never changes afterwards. Lowercase letters, digits and -. Left empty, it is made of the name.
            </span>
            <input id="assistant-id" type="text" maxlength="32" ?disabled=${locked} .value=${this.content.assistantId ?? ''} @input=${(event) => set('assistantId', event.target.value.trim())} />
          </div>
          <h2>Parameters</h2>
          ${this.schema
            ? html`<plane-params-form
                .schema=${this.schema}
                .value=${this.content.params}
                .errors=${this.errors}
                ?disabled=${locked}
                @params-change=${(event) => {
                  set('params', event.detail.value);
                  if (Object.keys(this.errors).length > 0) {
                    this.errors = validate(this.schema, this.content.params);
                  }
                }}
              ></plane-params-form>`
            : html`<p class="muted">Choose an image version to see its parameters.</p>`}
        </section>
        <section>
          <h2>Persona</h2>
          <p class="muted">A starting point: the customer rewrites it, except the operator rules. Leave in brackets what they must fill in.</p>
          <div class="field">
            <label for="operator-rules">${OPERATOR_RULES.title}</label>
            <span class="help">${OPERATOR_RULES.description}</span>
            <plane-markdown-input input-id="operator-rules" rows="5" ?disabled=${locked} .value=${this.content.operatorRules} @input=${(event) => set('operatorRules', event.target.value)}></plane-markdown-input>
          </div>
          ${PERSONA.map(
            (file) => html`
              <div class="field">
                <label for="persona-${file.key}"><code>${file.file}</code></label>
                <span class="help">${file.description}</span>
                <plane-markdown-input
                  input-id="persona-${file.key}"
                  rows="8"
                  ?disabled=${locked}
                  .value=${this.content.persona[file.key]}
                  @input=${(event) => set('persona', { ...this.content.persona, [file.key]: event.target.value })}
                ></plane-markdown-input>
              </div>
            `,
          )}
        </section>
        <div class="buttons">
          ${template?.builtIn ? null : html`<button class="primary" type="submit" ?disabled=${locked}>${template ? 'Save' : 'Create template'}</button>`}
          <a class="button" href="/templates">${template?.builtIn ? 'Back' : 'Cancel'}</a>
          ${this.saveError ? html`<span class="form-error" role="alert">${this.saveError}</span>` : null}
        </div>
      </form>
    `;
  }
}

customElements.define('plane-template-editor-page', TemplateEditorPage);
